/**
 * AB#1904 (W-1) — PostUpgradeBanner.
 *
 * One-time post-upgrade welcome banner on the Dashboard. Per Dina §4.5,
 * sits in the same banner slot as TrialBanner with a green-accent variant.
 *
 * Visibility logic:
 *   - Render only if `sessionStorage.devanza:subscriptionUpgraded` is present
 *     AND `localStorage.devanza.upgrade.bannerDismissed.{planId}` is NOT.
 *   - On mount: read-and-clear the sessionStorage flag (one-shot).
 *   - On dismiss: write a 7-day-TTL marker to localStorage.
 *   - Auto-dismiss after 7 days even if not interacted with.
 *
 * The banner takes precedence over a stale Trial banner — if an owner
 * upgrades during trial we show ONE banner, not two.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { trackEvent } from '../../lib/analytics';
import s from './PostUpgradeBanner.module.css';

const SESSION_KEY = 'devanza:subscriptionUpgraded';
const DISMISS_KEY_PREFIX = 'devanza.upgrade.bannerDismissed.';
const AUTO_DISMISS_DAYS = 7;

/**
 * AB#1933 — payload key renamed from `planId` to `planTier` because BE
 * `SubscriptionDto` no longer exposes a `planId`. Stale pre-fix banners
 * in sessionStorage are silently ignored by the type guard below — no
 * migration needed since the flag is one-shot per session anyway.
 */
interface UpgradedPayload {
    planTier: number;
    planName: string;
    timestamp: number;
}

function readSessionFlag(): UpgradedPayload | null {
    try {
        const raw = sessionStorage.getItem(SESSION_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as UpgradedPayload;
        if (typeof parsed?.planTier !== 'number' || typeof parsed?.planName !== 'string') {
            return null;
        }
        return parsed;
    } catch {
        return null;
    }
}

function readDismissTimestamp(planTier: number): number | null {
    try {
        const raw = localStorage.getItem(`${DISMISS_KEY_PREFIX}${planTier}`);
        if (!raw) return null;
        const ts = Number(raw);
        return Number.isFinite(ts) ? ts : null;
    } catch {
        return null;
    }
}

const PostUpgradeBanner: React.FC = () => {
    const { t } = useLanguage();
    const [payload, setPayload] = useState<UpgradedPayload | null>(null);

    useEffect(() => {
        const flag = readSessionFlag();
        if (!flag) return;

        // Check 7-day auto-dismiss window
        const dismissedAt = readDismissTimestamp(flag.planTier);
        const now = Date.now();
        if (dismissedAt && now - dismissedAt < AUTO_DISMISS_DAYS * 24 * 60 * 60 * 1000) {
            // Still within dismiss window — also clear the session flag so
            // we don't keep checking it on every Dashboard mount.
            try { sessionStorage.removeItem(SESSION_KEY); } catch { /* noop */ }
            return;
        }

        // Consume-and-clear the session flag — banner is a one-shot per
        // sessionStorage. New tab = no banner; refresh = banner persists
        // for this render but is cleared next time the page mounts.
        try { sessionStorage.removeItem(SESSION_KEY); } catch { /* noop */ }
        setPayload(flag);
        trackEvent('subscription.postUpgradeBannerShown', { toPlan: flag.planName });
    }, []);

    if (!payload) return null;

    const handleDismiss = () => {
        try {
            localStorage.setItem(`${DISMISS_KEY_PREFIX}${payload.planTier}`, String(Date.now()));
        } catch { /* noop */ }
        trackEvent('subscription.postUpgradeBannerDismissed', { toPlan: payload.planName });
        setPayload(null);
    };

    const handleCtaClick = () => {
        trackEvent('subscription.postUpgradeBannerCtaClicked', { toPlan: payload.planName });
    };

    return (
        <div className={s.banner} role="status">
            <span className={s.icon} aria-hidden="true">✨</span>
            <div className={s.message}>
                <p className={s.title}>
                    {t('subscription.banner.title').replace('{planName}', payload.planName)}
                </p>
            </div>
            <Link to="/my-business" className={s.action} onClick={handleCtaClick}>
                {t('subscription.banner.cta')}
            </Link>
            <button
                type="button"
                className={s.dismiss}
                onClick={handleDismiss}
                aria-label={t('subscription.banner.dismissAria')}
            >
                ×
            </button>
        </div>
    );
};

export default PostUpgradeBanner;
